import * as animate from '@js/util/animate.js'
import { select } from '@js/util/select.js'

const shouldDisableAnimations = matchMedia('(prefers-reduced-motion: reduce)').matches

class Accordion {
  heading = ''

  /**
   * @type {HTMLElement[]}
   */
  #elements = []

  /**
   * @type {string}
   */
  #ids

  /**
   * @type {Set<number>}
   */
  #currentIndexes = new Set()
  #initialized = false
  #duration = shouldDisableAnimations ? 0 : 300
  #reflectHash = true
  #exclusive = false

  #headerSelector = '.accordion-heading'
  #contentSelector = '.accordion-inner'

  /**
   * @returns {number[]}
   */
  get currentIndexes() {
    return [...this.#currentIndexes]
  }

  #getCurrentElements() {
    return [...this.#currentIndexes].map(index => this.#elements[index])
  }

  /**
   * @returns {Set<number>}
   */
  #getCurrentIndexesFromHash() {
    if (!this.#reflectHash) return new Set()

    let hashId = location.hash.replace(/^#/, '')
    if (hashId.length === 0) return new Set()

    const hashIds = hashId.split('/')
    const indexes = new Set(this.#ids.flatMap((id, index) => (hashIds.includes(id) ? index : [])))

    const remainingIds = hashIds.filter(id => !this.#ids.includes(id))
    for (const id of remainingIds) {
      let possiblyContainedElement = document.getElementById(id)
      if (!possiblyContainedElement) continue

      let index = 0
      for (let element of this.#elements) {
        if (element.contains(possiblyContainedElement)) {
          indexes.add(index)
        }
        index++
      }
    }

    if (this.#exclusive && indexes.size > 1) {
      return new Set([[...indexes].sort((a, b) => a - b)[0]])
    }

    return indexes
  }

  /**
   * @template {number[]|Set<number>} T
   * @param {T} indexes
   * @returns {T}
   */
  #revertIndexes(indexes) {
    const indexesArray = [...indexes]
    const revertedArray = this.#elements
      .map((_, index) => index)
      .filter(index => !indexesArray.includes(index))
    return Array.isArray(indexes) ? revertedArray : new Set(revertedArray)
  }

  #createEvent() {
    return new CustomEvent('accordion:toggled', {
      bubbles: true,
    })
  }

  constructor(container, { reflectHash = true, exclusive = false } = {}) {
    this.#reflectHash = reflectHash
    this.#exclusive = exclusive
    this.#elements = [...container.querySelectorAll('.accordion')]
    this.#elements.forEach((element, index) => {
      element.querySelector(this.#contentSelector).style.display = 'none'
      let headerElement = element.querySelector(this.#headerSelector)
      headerElement.addEventListener('click', event => {
        this.toggle(index)
      })
      headerElement.addEventListener('keydown', event => {
        if (event.key === 'ArrowUp') {
          event.preventDefault()
          let newIndex = (index - 1 + this.#elements.length) % this.#elements.length
          this.#elements[newIndex].querySelector(this.#headerSelector).focus()
        }

        if (event.key === 'ArrowDown') {
          event.preventDefault()
          let newIndex = (index + 1) % this.#elements.length
          this.#elements[newIndex].querySelector(this.#headerSelector).focus()
        }

        if (event.key === 'Home') {
          event.preventDefault()
          let newIndex = 0
          this.#elements[newIndex].querySelector(this.#headerSelector).focus()
        }

        if (event.key === 'End') {
          event.preventDefault()
          let newIndex = this.#elements.length - 1
          this.#elements[newIndex].querySelector(this.#headerSelector).focus()
        }
      })
      headerElement.setAttribute('aria-expanded', 'false')
    })

    this.#ids = this.#elements.map(el => el.id)

    let indexes = this.#getCurrentIndexesFromHash()

    if (indexes.size > 0) {
      for (const index of indexes) {
        this.#elements[index].classList.add('accordion-open')
        this.#elements[index]
          .querySelector(this.#headerSelector)
          .setAttribute('aria-expanded', 'true')
        this.#elements[index].querySelector(this.#contentSelector).style.display = 'block'
      }

      this.#currentIndexes = indexes
    }

    this.#initialized = true

    if (typeof index === 'number') {
      this.#elements[index].dispatchEvent(this.#createEvent())
    }

    if (this.#reflectHash) {
      window.addEventListener('popstate', () => {
        const openIndexes = this.#getCurrentIndexesFromHash()
        const closedIndexes = this.#revertIndexes(openIndexes)

        for (const openIndex of openIndexes) {
          this.open(openIndex, { push: false, forceScroll: true })
        }
        for (const closedIndex of closedIndexes) {
          this.close(closedIndex, { push: false })
        }
      })
    }
  }

  open(index, options) {
    this.toggle(index, true, options)
  }

  close(index, options) {
    this.toggle(index, false, options)
  }

  toggle(index, state, { push = true, forceScroll = false } = {}) {
    push &&= this.#reflectHash

    let targetElement = this.#elements[index]
    if (!targetElement) return

    let currentState = this.#currentIndexes.has(index)
    let newState = typeof state === 'boolean' ? state : !currentState

    if (newState === currentState) return

    let targetContentElement = targetElement.querySelector(this.#contentSelector)
    let targetHeaderElement = targetElement.querySelector(this.#headerSelector)

    let needsToScroll = false
    let scrollTarget

    if (newState) {
      targetElement.classList.add('accordion-open')
      targetHeaderElement.setAttribute('aria-expanded', 'true')

      if (this.#currentIndexes.size > 0 && this.#exclusive) {
        let currentElement = this.#getCurrentElements()[0]
        let currentContentElement = currentElement.querySelector(this.#contentSelector)
        let currentHeaderElement = currentElement.querySelector(this.#headerSelector)

        currentElement.classList.remove('accordion-open')
        currentHeaderElement.setAttribute('aria-expanded', 'false')

        // Calculate scroll position of the new open accordion after closing the current one
        // This allows scrolling to the top of the new accordion if needed
        currentContentElement.style.display = 'none'
        targetContentElement.style.display = 'block'
        let upcomingUpperEdge = targetHeaderElement.getBoundingClientRect().top
        if (upcomingUpperEdge < 0) {
          needsToScroll = true
        }
        scrollTarget =
          window.scrollY +
          upcomingUpperEdge -
          parseFloat(getComputedStyle(targetHeaderElement).scrollMarginTop)
        targetContentElement.style.display = 'none'
        currentContentElement.style.display = 'block'

        animate.slideUp(currentContentElement, { duration: this.#duration }).then(() => {
          currentElement.dispatchEvent(this.#createEvent())
        })

        this.#currentIndexes.clear()
      } else {
        scrollTarget =
          window.scrollY +
          targetHeaderElement.getBoundingClientRect().top -
          parseFloat(getComputedStyle(targetHeaderElement).scrollMarginTop)
      }

      animate.slideDown(targetContentElement, { duration: this.#duration }).then(() => {
        targetElement.dispatchEvent(this.#createEvent())
      })

      if (needsToScroll || forceScroll) {
        window.scrollTo({
          top: scrollTarget,
          behavior: shouldDisableAnimations ? 'instant' : 'smooth',
        })
      }

      this.#currentIndexes.add(index)
    } else {
      targetElement.classList.remove('accordion-open')
      targetHeaderElement.setAttribute('aria-expanded', 'false')

      animate.slideUp(targetContentElement, { duration: this.#duration }).then(() => {
        targetElement.dispatchEvent(this.#createEvent())
      })

      this.#currentIndexes.delete(index)
    }

    if (this.#initialized && push) {
      const hashId = [...this.#currentIndexes]
        .sort((a, b) => a - b)
        .map(index => this.#elements[index].id)
        .join('/')

      window.history.pushState(
        null,
        null,
        window.location.pathname + window.location.search + (hashId.length > 0 ? `#${hashId}` : ''),
      )
    }
  }
}

select('.accordion-wrapper', element => {
  const group = element.querySelector('.accordion-group')
  const reflectHash = group.dataset.reflectHash === 'true'
  const exclusive = group.dataset.exclusive === 'true'

  const accordion = new Accordion(group, {
    reflectHash,
    exclusive,
  })

  const linkWrapper = element.querySelector('.accordion-prepended-links')
  if (linkWrapper) {
    for (const link of linkWrapper.children) {
      const index = [...linkWrapper.children].indexOf(link)
      link.addEventListener('click', event => {
        if (index === accordion.currentIndex) return

        event.preventDefault()
        accordion.open(index, { push: true, forceScroll: true })
      })
    }
  }
})
